import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/auth-migration-helper';
import { NotificationService } from '../services/notification-service';
import { log } from '../services/production-logger';
import {
  Notification, 
  NotificationFilter, 
  NotificationStats,
  NotificationType,
  NotificationData,
  NotificationPriority 
} from '../types/notification';

export const useNotifications = (filter?: NotificationFilter, limit: number = 50) => {
  const { activeOrganization, currentUser } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to notifications
  useEffect(() => {
    if (!activeOrganization || !currentUser) {
      log.debug('🔔 useNotifications: No organization or user, clearing notifications');
      setNotifications([]);
      setStats(null);
      return;
    }

    log.debug('🔔 useNotifications: Setting up subscription', {
      organizationId: activeOrganization.id,
      userId: currentUser.uid,
      filter,
      limit
    });

    setLoading(true);
    setError(null);

    const unsubscribe = NotificationService.subscribeToNotifications(
      activeOrganization.id,
      currentUser.uid,
      (notificationList) => {
        log.debug('🔔 useNotifications: Received notifications', {
          count: notificationList.length,
          unreadCount: notificationList.filter(n => !n.read).length
        });
        setNotifications(notificationList);
        setLoading(false);
      },
      filter,
      limit
    );

    // Also load stats
    loadStats();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [activeOrganization, currentUser, filter, limit]);

  const loadStats = useCallback(async () => {
    if (!activeOrganization || !currentUser) return;

    try {
      const notificationStats = await NotificationService.getNotificationStats(
        activeOrganization.id,
        currentUser.uid
      );
      setStats(notificationStats);
    } catch (err) {
      log.error('Error loading notification stats:', err);
      setError('Failed to load notification statistics');
    }
  }, [activeOrganization, currentUser]);

  const createNotification = useCallback(async (
    type: NotificationType,
    title: string,
    message: string,
    data?: NotificationData,
    priority: NotificationPriority = 'medium'
  ) => {
    if (!activeOrganization || !currentUser) {
      throw new Error('No active organization or user');
    }

    try {
      const notificationId = await NotificationService.createNotification(
        activeOrganization.id,
        currentUser.uid,
        type,
        title,
        message,
        data,
        priority
      );
      
      // Reload stats after creating
      loadStats();
      
      return notificationId;
    } catch (err) {
      log.error('Error creating notification:', err);
      throw err;
    }
  }, [activeOrganization, currentUser, loadStats]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await NotificationService.markAsRead(notificationId);
      // Update local state optimistically
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      loadStats();
    } catch (err) {
      log.error('Error marking notification as read:', err);
      throw err;
    }
  }, [loadStats]);

  const markAllAsRead = useCallback(async () => {
    if (!activeOrganization || !currentUser) return;

    try {
      await NotificationService.markAllAsRead(activeOrganization.id, currentUser.uid);
      // Update local state optimistically
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      loadStats();
    } catch (err) {
      log.error('Error marking all notifications as read:', err);
      throw err;
    }
  }, [activeOrganization, currentUser, loadStats]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await NotificationService.deleteNotification(notificationId);
      // Update local state optimistically
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      loadStats();
    } catch (err) {
      log.error('Error deleting notification:', err);
      throw err;
    }
  }, [loadStats]);

  const deleteReadNotifications = useCallback(async () => {
    if (!activeOrganization || !currentUser) return;

    try {
      await NotificationService.deleteReadNotifications(activeOrganization.id, currentUser.uid);
      // Update local state optimistically
      setNotifications(prev => prev.filter(n => !n.read));
      loadStats();
    } catch (err) {
      log.error('Error deleting read notifications:', err);
      throw err;
    }
  }, [activeOrganization, currentUser, loadStats]);

  // Helper methods for creating specific notification types
  const createInvoiceNotification = useCallback(async (
    type: 'invoice_created' | 'invoice_updated' | 'invoice_paid' | 'invoice_overdue',
    invoiceId: string,
    supplierName?: string,
    amount?: number,
    currency?: string
  ) => {
    if (!activeOrganization || !currentUser) return;

    return NotificationService.createInvoiceNotification(
      activeOrganization.id,
      currentUser.uid,
      type,
      invoiceId,
      supplierName,
      amount,
      currency
    );
  }, [activeOrganization, currentUser]);

  const createSupplierNotification = useCallback(async (
    type: 'supplier_added' | 'supplier_updated',
    supplierId: string,
    supplierName: string
  ) => {
    if (!activeOrganization || !currentUser) return;

    return NotificationService.createSupplierNotification(
      activeOrganization.id,
      currentUser.uid,
      type,
      supplierId,
      supplierName
    );
  }, [activeOrganization, currentUser]);

  const createPaymentNotification = useCallback(async (
    type: 'payment_received' | 'payment_failed',
    amount: number,
    currency: string,
    invoiceId?: string
  ) => {
    if (!activeOrganization || !currentUser) return;

    return NotificationService.createPaymentNotification(
      activeOrganization.id,
      currentUser.uid,
      type,
      amount,
      currency,
      invoiceId
    );
  }, [activeOrganization, currentUser]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const hasUnread = unreadCount > 0;
  
  // Debug logging
  if (notifications.length > 0) {
    log.debug('🔔 useNotifications: Current state', {
      totalNotifications: notifications.length,
      unreadCount,
      hasUnread
    });
  }

  return {
    notifications,
    stats,
    loading,
    error,
    unreadCount,
    hasUnread,
    
    // Actions
    createNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteReadNotifications,
    
    // Helpers
    createInvoiceNotification,
    createSupplierNotification,
    createPaymentNotification,
    
    // Utilities
    refreshStats: loadStats,
  };
};
